import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Payment, ApiResponse, CreatePaymentRequest } from '../../types';

interface PaymentsState {
  payments: Payment[];
  currentPayment: Payment | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

const initialState: PaymentsState = {
  payments: [],
  currentPayment: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
};

export const fetchPayments = createAsyncThunk(
  'payments/fetchPayments',
  async ({ page = 1, pageSize = 20, filters }: { page?: number; pageSize?: number; filters?: any }) => {
    // Convert page-based pagination to offset-based for backend
    const offset = (page - 1) * pageSize;
    const params = new URLSearchParams({
      limit: pageSize.toString(),
      offset: offset.toString(),
      ...filters,
    });
    
    console.log('🔍 Fetching payments with params:', {
      page,
      pageSize,
      offset,
      limit: pageSize,
      filters,
      url: `/payments?${params}`,
      apiBaseUrl: (window as any).API_BASE_URL || 'not set'
    });
    
    const response = await api.get<ApiResponse<any>>(
      `/payments?${params}`
    );
    
    console.log('📥 Payments API response:', {
      success: response.data.success,
      paymentsCount: response.data.data?.payments?.length || 0,
      total: response.data.data?.pagination?.total || 0,
      status: response.status,
      fullResponse: response.data
    });
    
    // Handle the actual backend response structure
    const payments = response.data.data?.payments || response.data.data || [];
    const total = response.data.data?.pagination?.total || response.data.pagination?.total || 0;
    
    console.log('📊 Processed payments data:', { payments, total });
    
    return { 
      payments, 
      total,
      page, 
      pageSize 
    };
  }
);

export const fetchPaymentById = createAsyncThunk(
  'payments/fetchPaymentById',
  async (paymentId: string) => {
    const response = await api.get<ApiResponse<Payment>>(`/payments/${paymentId}`);
    return response.data.data;
  }
);

export const createPayment = createAsyncThunk(
  'payments/createPayment',
  async (paymentData: CreatePaymentRequest) => {
    // Ensure required fields are present
    const requiredFields = ['customerId', 'amount', 'productId', 'productName'];
    const missingFields = requiredFields.filter(field => !paymentData[field as keyof CreatePaymentRequest]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Prepare payment data with defaults
    const requestData = {
      customerId: paymentData.customerId,
      amount: paymentData.amount,
      productId: paymentData.productId,
      productName: paymentData.productName,
      currency: paymentData.currency || 'JPY',
      description: paymentData.description || `Payment for ${paymentData.productName}`,
      returnUrl: paymentData.returnUrl
    };

    console.log('💳 Creating payment with data:', requestData);
    
    const response = await api.post<ApiResponse<Payment>>('/payments/one-time', requestData);
    return response.data.data;
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch payments
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.payments;
        state.totalCount = action.payload.total;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payments';
      })
      // Fetch payment by ID
      .addCase(fetchPaymentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.loading = false;
        state.currentPayment = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payment';
      })
      // Create payment
      .addCase(createPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action: PayloadAction<Payment>) => {
        state.loading = false;
        state.payments.unshift(action.payload);
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create payment';
      });
  },
});

export const { clearError, clearCurrentPayment } = paymentsSlice.actions;
export default paymentsSlice.reducer;